import { Layers, IconLayerOptions, SelectedCircleOptions } from '../models';
import { MapLayerRenderer } from '../mapbox-map-layer-renderer';
import { MapboxHelper } from '../mapbox-map-helper';
import { Theme } from 'src/app/modules/shared/models';
import { VEHICLE_ICON } from 'src/app/modules/shared/models/vehicle';
import { CircleLayerOptions } from '../models/circle-layer-options';

export class RenderVehiclesLayer extends MapLayerRenderer {
    constructor(
        protected _mapboxHelper: MapboxHelper,
        private _theme: Theme = Theme.Light,
    ) {
        super(_mapboxHelper);
    }

    protected modifyFeatureCollection(
        fc: GeoJSON.FeatureCollection,
    ): GeoJSON.FeatureCollection {
        return fc;
    }

    private _getFirstSymbolLayer(map: mapboxgl.Map): string | undefined {
        if (!map) {
            return undefined;
        }

        try {
            const style = map.getStyle();
            if (!style || !style.layers) {
                return undefined;
            }

            // Find layers that are specifically place/city labels (not road shields)
            // These typically have 'place', 'settlement', or specific label types in their ID
            for (const layer of style.layers) {
                if (layer.type === 'symbol') {
                    const layerId = layer.id.toLowerCase();
                    if (
                        layerId.includes('place-') ||
                        layerId.includes('settlement') ||
                        layerId.includes('city-label') ||
                        layerId.includes('town-label')
                    ) {
                        return layer.id;
                    }
                }
            }
        } catch (error) {
            // Style might not be fully ready yet
            return undefined;
        }

        return undefined;
    }

    protected renderLayers(map: mapboxgl.Map): void {
        const layers = {};
        layers[Layers.Vehicle] = [];
        layers[Layers.VehicleTooltip] = [];

        const vehicleIconlayerId = `${Layers.Vehicle}`;
        if (!this._mapboxHelper.layerExists(map, vehicleIconlayerId)) {
            let layerNames = [];
            const options = this._vehicleOptions(map);
            layerNames = this._mapboxHelper.renderIcon(
                map,
                this._sourceId,
                this._sourceLayer,
                vehicleIconlayerId,
                options,
            );
            layers[Layers.Vehicle] = layers[Layers.Vehicle].concat(layerNames);
        }

        const clickableLayer = `${Layers.Vehicle}-clickable`;
        if (!this._mapboxHelper.layerExists(map, clickableLayer)) {
            let layerNames = [];

            const options = this._clickableLayer(map);
            layerNames = this._mapboxHelper.renderCircle(
                map,
                this._sourceId,
                this._sourceLayer,
                clickableLayer,
                options,
            );

            layers[Layers.Vehicle] = layers[Layers.Vehicle].concat(layerNames);
        }

        const vehicleWarninglayerId = `${Layers.VehicleWarning}`;
        if (!this._mapboxHelper.layerExists(map, vehicleWarninglayerId)) {
            let layerNames = [];

            const options = this._vehicleWarningLayer(map);
            layerNames = this._mapboxHelper.renderIcon(
                map,
                this._sourceId,
                this._sourceLayer,
                vehicleWarninglayerId,
                options,
            );

            layers[Layers.Vehicle] = layers[Layers.Vehicle].concat(layerNames);
        }

        const vehicleBadgeLayerId = 'vehicle-badge';
        if (!this._mapboxHelper.layerExists(map, vehicleBadgeLayerId)) {
            const options = this._vehicleTextBadgeOptions(map);
            const layerNames = this._mapboxHelper.renderIcon(
                map, this._sourceId, this._sourceLayer, vehicleBadgeLayerId, options,
            );
            layers[Layers.Vehicle] = layers[Layers.Vehicle].concat(layerNames);
        }

        this.addLayerNames(layers);
    }

    private _vehicleOptions(map: mapboxgl.Map): IconLayerOptions {
        const options = new IconLayerOptions();
        options.icon = {
            type: 'identity',
            property: 'icon',
            default: VEHICLE_ICON.Default,
        };
        options.interactive = false;
        // Insert vehicle layer before first symbol layer to keep city labels on top
        options.layerBefore = this._getFirstSymbolLayer(map);
        options.iconSize = [
            'interpolate',
            ['linear'],
            ['zoom'],
            10,
            0.3,
            12,
            1,
            22,
            1,
        ];
        options.visibleFrom = 6;
        options.visibility = true;
        options.useIconFromProperties = false;
        // Rotation is applied by Mapbox GPU (icon-rotate reads the 'bearing' feature property).
        // VehicleIcon renders the arrow pointing north; Mapbox rotates it to the travel bearing.
        options.iconRotate = true;
        options.symbolSortKey = 'zIndex';
        options.selectedCircleOptions = new SelectedCircleOptions();
        options.selectedCircleOptions.color = {
            type: 'identity',
            property: 'deviation-color',
            default: this._theme === Theme.Light ? '#000000' : '#ffffff',
        };

        return options;
    }

    private _vehicleTextBadgeOptions(map: mapboxgl.Map): IconLayerOptions {
        const options = new IconLayerOptions();
        options.icon = { type: 'identity', property: 'textBadge', default: '' };
        options.interactive = false;
        options.layerBefore = this._getFirstSymbolLayer(map);
        options.iconSize = [
            'interpolate',
            ['linear'],
            ['zoom'],
            10,
            0.3,
            12,
            1,
            22,
            1,
        ];
        options.visibleFrom = 6;
        options.visibility = true;
        options.useIconFromProperties = false;
        options.iconRotate = false;
        options.iconRotationAlignment = 'viewport';
        options.symbolSortKey = 'zIndex';
        options.iconOpacity = ['case', ['has', 'textBadge'], 1, 0];
        return options;
    }

    private _clickableLayer(map: mapboxgl.Map): CircleLayerOptions {
        const options = new CircleLayerOptions();
        options.visibility = true;
        options.color = 'transparent';
        options.layerBefore = this._getFirstSymbolLayer(map);
        options.radius = {
            base: 1,
            stops: [
                [10, 4],
                [12, 14],
                [22, 16],
            ],
        };
        options.strokeWidth = 1;
        options.strokeColor = 'transparent';
        options.visibleFrom = 6;
        options.visibleTo = 22;
        return options;
    }

    private _vehicleWarningLayer(map: mapboxgl.Map): IconLayerOptions {
        const options = new IconLayerOptions();
        options.interactive = false;
        options.visibility = true;
        options.layerBefore = this._getFirstSymbolLayer(map);
        options.visibleFrom = 10;
        options.visibleTo = 22;
        options.icon = 'warning-icon';
        options.iconSize = [
            'interpolate',
            ['linear'],
            ['zoom'],
            10,
            1.2,
            12,
            1.7,
            22,
            1.7,
        ];
        options.iconRotate = false;
        options.useIconFromProperties = false;
        options.iconOffset = [9, 10];
        options.iconOpacity = [
            'case',
            ['==', ['get', 'hasWarning'], true],
            1,
            ['==', ['get', 'hasWarning'], false],
            0,
            1,
        ];

        return options;
    }
}
